/**
 * Status instances on a combatant — apply/remove, the GM-authored `grant_ruling`, and the
 * whole-array `restore_statuses`. The facet carry-through both restoring inverses share lives
 * in `./context`'s `statusRestoreFacets`.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import type { EncounterCommand } from "../commands";
import { FLYING_STATUS, voluntaryMoveBar } from "../movement";
import { makeStatusInstance, rawStatusDefaults } from "../types";
import { changed, requireCombatant, statusRestoreFacets, withCombatant, type EncounterHandler } from "./context";

export const applyApplyStatus: EncounterHandler<"apply_status"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  if (c.statuses.some((s) => s.name === cmd.status)) {
    return err(
      new CommandError(`apply_status: combatant ${c.id} already has status "${cmd.status}"`),
    );
  }
  // Every facet rides the command verbatim — a fresh manual apply sets a few (`partner`,
  // `source`, `expiry`, `appliedTurn`), and `remove_status`'s own inverse-builder sets all of
  // them, `suspended` included. The builder owns the `until_removed` default and the
  // never-default rule for the rest. Spreading the whole command means a facet the inverse
  // captures (`statusRestoreFacets`, pinned in `./context`) reaches the builder with no
  // per-facet line here to forget.
  // A GM apply that names no expiry takes the status's fixed RAW duration when it has one.
  // Deterministic on redo: the same command re-applies against the same state.
  const rawDefault = cmd.expiry === undefined ? rawStatusDefaults(cmd.status, state.activation.turnOrdinal) : undefined;
  const instance = makeStatusInstance({ ...cmd, ...rawDefault, ...(cmd.appliedTurn !== undefined ? { appliedTurn: cmd.appliedTurn } : {}), name: cmd.status });
  // Fall-trigger 2: landing
  // ANY can't-move status on a `Flying` carrier grounds it at that same instant — a
  // co-effect, not a separate command, so it's undoable for free as one atomic
  // apply. "Can't-move" is read off the SAME shared bar {@link voluntaryMoveBar}
  // consults (Immobilized/Stunned/Shut Down/Down and Out) so this never drifts from
  // the move-legality wall — computed on the status list AFTER the new instance
  // lands, so a status that itself imposes the bar (or stacks onto an existing one)
  // both ground correctly. `cmd.status === FLYING_STATUS` can't reach here already
  // Flying (the duplicate-status reject above), so no extra guard is needed.
  const flying = c.statuses.find((s) => s.name === FLYING_STATUS);
  const grounds =
    flying !== undefined && voluntaryMoveBar({ statuses: [...c.statuses, instance] }) != null;
  const statuses = grounds
    ? [...c.statuses, instance].filter((s) => s.name !== FLYING_STATUS)
    : [...c.statuses, instance];
  const next = withCombatant(state, { ...c, statuses });
  const inverse: EncounterCommand = grounds
    ? {
        type: "batch",
        commands: [
          { type: "remove_status", actorId: cmd.actorId, combatantId: c.id, status: cmd.status },
          {
            type: "apply_status",
            actorId: cmd.actorId,
            combatantId: c.id,
            status: FLYING_STATUS,
            ...statusRestoreFacets(flying),
          },
        ],
      }
    : { type: "remove_status", actorId: cmd.actorId, combatantId: c.id, status: cmd.status };
  return ok({
    state: next,
    inverse,
    events: changed(c.id),
  });
};

export const applyRemoveStatus: EncounterHandler<"remove_status"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const removed = c.statuses.find((s) => s.name === cmd.status);
  if (!removed) {
    return err(
      new CommandError(`remove_status: combatant ${c.id} has no status "${cmd.status}"`),
    );
  }
  const next = withCombatant(state, {
    ...c,
    statuses: c.statuses.filter((s) => s.name !== cmd.status),
  });
  return ok({
    state: next,
    inverse: {
      // Restore the removed instance's expiry (+ partner/source, if any) so an undo is
      // byte-identical.
      type: "apply_status",
      actorId: cmd.actorId,
      combatantId: c.id,
      status: cmd.status,
      ...statusRestoreFacets(removed),
    },
    events: changed(c.id),
  });
};

export const applyGrantRuling: EncounterHandler<"grant_ruling"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  // Replace (or stack onto), don't reject, a same-named instance: re-granting is
  // what a GM does at the table. The whole prior array rides the inverse, so whatever this
  // displaces comes back on undo — no facet list to keep in sync.
  const prior = c.statuses;
  // DEEP COPY, not `cmd.ruling` — the granted status must be a SNAPSHOT. A GM firing a
  // ruling out of a saved library hands us the library entry's OWN spec object; storing it
  // by reference aliases every granted status to that entry, so editing the entry silently
  // rewrites live statuses mid-encounter. A shallow spread is not enough —
  // `extraDamage.dice` is nested. Pinned by the aliasing test in `grantRuling.test.ts`. The
  // clone stays HERE, not in the builder: the undo replays of `apply_status`/`status_add`
  // must restore the captured object as-is.
  //
  // `source` is deliberately LEFT UNSET (the command has no such field). A GM ruling has no
  // combatant source, and `StatusInstance.source` is read by three consumers expecting a real
  // combatant (`source`-keyed expiry, the authored-offer gate, the third-party reaction
  // router). Stamping the GM's actor id here would be a bare-string `CombatantId` that names
  // no combatant. `statusHandlersFromRefs` already handles the absent case.
  //
  // `stack` folds the new grant into a same-named ruling's count rather than replacing it; the
  // newest grant's spec (reason, expiry) wins, as it does on a replace.
  const stackedOn = cmd.stack === true ? prior.find((s) => s.name === cmd.status && s.ruling !== undefined)?.ruling : undefined;
  const ruling = structuredClone(cmd.ruling);
  if (stackedOn !== undefined) ruling.stacks = (stackedOn.stacks ?? 1) + (cmd.ruling.stacks ?? 1);
  const instance = makeStatusInstance({
    name: cmd.status,
    expiry: cmd.expiry,
    appliedTurn: cmd.appliedTurn,
    ruling,
  });
  const next = withCombatant(state, {
    ...c,
    statuses: [...prior.filter((s) => s.name !== cmd.status), instance],
  });
  return ok({
    state: next,
    inverse: { type: "restore_statuses", actorId: cmd.actorId, combatantId: c.id, statuses: prior },
    events: changed(c.id),
  });
};

export const applyRestoreStatuses: EncounterHandler<"restore_statuses"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  // Unvalidated, like every internal `restore_*` — it writes back a list this same reducer
  // produced. Its own inverse captures the pre-restore array, so redo round-trips too.
  const prior = c.statuses;
  const next = withCombatant(state, { ...c, statuses: cmd.statuses });
  return ok({
    state: next,
    inverse: { type: "restore_statuses", actorId: cmd.actorId, combatantId: c.id, statuses: prior },
    events: changed(c.id),
  });
};
